import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiQrCode, FiExternalLink, FiShare2, FiDownload } = FiIcons;

const QRScanner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      handleScan();
    }
  }, [id]);

  const handleScan = async () => {
    try {
      setLoading(true);
      
      // Track the scan
      const response = await fetch(`/api/qr/scan/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          referrer: document.referrer
        })
      });

      if (response.ok) {
        const data = await response.json();
        setQrData(data);
        
        // Redirect based on QR type
        if (data.type === 'url' && data.redirectUrl) {
          // Small delay to show loading, then redirect
          setTimeout(() => {
            window.location.href = data.redirectUrl;
          }, 1000);
        }
      } else {
        setError('QR code not found or expired');
      }
    } catch (err) {
      setError('Failed to process QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleManualRedirect = () => {
    if (qrData?.redirectUrl) {
      window.location.href = qrData.redirectUrl;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md mx-4"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing QR Code</h2>
          <p className="text-gray-600">Please wait while we process your request...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md mx-4"
        >
          <SafeIcon icon={FiQrCode} className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">QR Code Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Go to Homepage
          </button>
        </motion.div>
      </div>
    );
  }

  if (qrData?.type === 'url') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md mx-4"
        >
          <SafeIcon icon={FiExternalLink} className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Redirecting...</h2>
          <p className="text-gray-600 mb-6">
            You will be redirected to: <br />
            <span className="font-medium text-blue-600">{qrData.redirectUrl}</span>
          </p>
          
          <div className="flex flex-col space-y-3">
            <button
              onClick={handleManualRedirect}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Continue to Website
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium"
            >
              Go to Homepage
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // For other QR types (vCard, WiFi, etc.), show the content
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-4"
      >
        <div className="text-center mb-6">
          <SafeIcon icon={FiQrCode} className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{qrData?.title}</h2>
          <p className="text-gray-600">QR Code Content</p>
        </div>

        <div className="space-y-4">
          {qrData?.type === 'vcard' && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Contact Information</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Name:</span> {qrData.data.name}</p>
                <p><span className="font-medium">Phone:</span> {qrData.data.phone}</p>
                <p><span className="font-medium">Email:</span> {qrData.data.email}</p>
                <p><span className="font-medium">Organization:</span> {qrData.data.organization}</p>
              </div>
            </div>
          )}

          {qrData?.type === 'wifi' && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">WiFi Network</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Network:</span> {qrData.data.ssid}</p>
                <p><span className="font-medium">Security:</span> {qrData.data.security}</p>
                <p className="text-xs text-gray-500 mt-2">
                  Your device should automatically connect to this network.
                </p>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={() => navigator.share && navigator.share({ 
                title: qrData?.title, 
                url: window.location.href 
              })}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              <SafeIcon icon={FiShare2} className="w-4 h-4 mr-2" />
              Share
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default QRScanner;